import React, { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { questionService, Question } from '../../firebase/questionService';
import { FaThumbsUp, FaThumbsDown } from 'react-icons/fa';

const DIFFICULTIES = ['easy', 'medium', 'hard'] as const;
const DOLLAR_VALUES = {
  easy: ['$100', '$200'],
  medium: ['$300', '$400'],
  hard: ['$500']
} as const;

type SortColumn = 'category' | 'difficulty' | 'dollarValue';
type SortDirection = 'asc' | 'desc';

const AdminQuestions = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    text: '',
    answer: '',
    categoryId: '',
    difficulty: 'easy',
    choices: [''],
    isFinalJeopardy: false,
    isActive: true,
  });
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [updatingCategoryId, setUpdatingCategoryId] = useState<string | null>(null);

  // Multi-column sort state
  const [sorts, setSorts] = useState<{ column: SortColumn; direction: SortDirection }[]>([]);

  const [votedQuestions, setVotedQuestions] = useState<{ [id: string]: boolean }>({});

  useEffect(() => {
    fetchCategories();
    fetchQuestions();
  }, []);

  async function fetchCategories() {
    const cats = await questionService.getCategories();
    setCategories(cats);
  }

  async function fetchQuestions() {
    const qs = await questionService.getQuestions();
    setQuestions(qs);
  }

  function openModal() {
    setEditingQuestion(null);
    setForm({ text: '', answer: '', categoryId: '', difficulty: 'easy', choices: [''], isFinalJeopardy: false, isActive: true });
    setModalOpen(true);
  }

  function openEditModal(q: Question) {
    setEditingQuestion(q);
    setForm({
      text: q.text,
      answer: q.answer,
      categoryId: q.categoryId,
      difficulty: q.difficulty,
      choices: q.choices && q.choices.length > 0 ? q.choices : [''],
      isFinalJeopardy: q.isFinalJeopardy || false,
      isActive: q.isActive !== false,
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingQuestion(null);
  }

  function handleChoiceChange(idx: number, value: string) {
    setForm(f => {
      const newChoices = [...f.choices];
      newChoices[idx] = value;
      return { ...f, choices: newChoices };
    });
  }

  function addChoice() {
    setForm(f => ({ ...f, choices: [...f.choices, ''] }));
  }

  function removeChoice(idx: number) {
    setForm(f => ({ ...f, choices: f.choices.filter((_, i) => i !== idx) }));
  }

  async function handleSave() {
    setSaving(true);
    const data = {
      text: form.text,
      answer: form.answer,
      categoryId: form.categoryId,
      difficulty: form.difficulty as 'easy' | 'medium' | 'hard',
      choices: form.choices.filter(c => c.trim() !== ''),
      isFinalJeopardy: form.isFinalJeopardy,
      isActive: form.isActive
    };
    if (editingQuestion && editingQuestion.id) {
      await questionService.updateQuestion(editingQuestion.id, data);
    } else {
      await questionService.addQuestion(data);
    }
    setSaving(false);
    closeModal();
    fetchQuestions();
  }

  async function handleDelete() {
    if (deleteId) {
      await questionService.deleteQuestion(deleteId);
      setDeleteId(null);
      fetchQuestions();
    }
  }

  function getCategoryName(id: string) {
    return categories.find(c => c.id === id)?.name || '';
  }

  // Multi-column sort logic
  function handleSort(column: SortColumn) {
    setSorts(prev => {
      const existing = prev.find(s => s.column === column);
      if (existing) {
        // Toggle direction
        return prev.map(s =>
          s.column === column ? { ...s, direction: s.direction === 'asc' ? 'desc' : 'asc' } : s
        );
      } else {
        // Add as primary sort
        return [{ column, direction: 'asc' }, ...prev];
      }
    });
  }

  function getSortIndicator(column: SortColumn) {
    const sort = sorts.find(s => s.column === column);
    if (!sort) return null;
    return sort.direction === 'asc' ? '▲' : '▼';
  }

  function multiColumnSort(a: Question, b: Question): number {
    for (const sort of sorts) {
      if (sort.column === 'category') {
        const catA = getCategoryName(a.categoryId).toLowerCase();
        const catB = getCategoryName(b.categoryId).toLowerCase();
        if (catA < catB) return sort.direction === 'asc' ? -1 : 1;
        if (catA > catB) return sort.direction === 'asc' ? 1 : -1;
      } else if (sort.column === 'difficulty') {
        const diffOrder = { easy: 0, medium: 1, hard: 2 };
        const diffA = diffOrder[a.difficulty as 'easy' | 'medium' | 'hard'];
        const diffB = diffOrder[b.difficulty as 'easy' | 'medium' | 'hard'];
        if (diffA < diffB) return sort.direction === 'asc' ? -1 : 1;
        if (diffA > diffB) return sort.direction === 'asc' ? 1 : -1;
      } else if (sort.column === 'dollarValue') {
        const dollarValueA = getDollarValue(a.difficulty, 0);
        const dollarValueB = getDollarValue(b.difficulty, 0);
        if (dollarValueA < dollarValueB) return sort.direction === 'asc' ? -1 : 1;
        if (dollarValueA > dollarValueB) return sort.direction === 'asc' ? 1 : -1;
      }
    }
    return 0;
  }

  const sortedQuestions = [...questions].sort(multiColumnSort);

  async function handleVote(qid: string, value: number) {
    if (votedQuestions[qid]) return;
    await questionService.rateQuestion(qid, value);
    setQuestions((prev) => prev.map(q => q.id === qid ? { ...q, rating: value === 1 ? ((q.rating || 0) * (q.ratingCount || 0) + 1) / ((q.ratingCount || 0) + 1) : ((q.rating || 0) * (q.ratingCount || 0)) / ((q.ratingCount || 0) + 1), ratingCount: (q.ratingCount || 0) + 1 } : q));
    setVotedQuestions((prev) => ({ ...prev, [qid]: true }));
  }

  function getDollarValue(difficulty: string, index: number): string {
    if (difficulty === 'easy') return DOLLAR_VALUES.easy[index] || '';
    if (difficulty === 'medium') return DOLLAR_VALUES.medium[index] || '';
    if (difficulty === 'hard') return DOLLAR_VALUES.hard[0] || '';
    return '';
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-brand-yellow">Questions</h2>
        <button onClick={openModal} className="bg-brand-yellow text-black font-bold px-6 py-2 rounded-lg hover:bg-[#fccd6c]/90 transition-colors">
          + Add Question
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full bg-[#222] rounded-xl overflow-hidden shadow-lg mb-8">
          <thead>
            <tr className="bg-[#333] text-brand-yellow text-sm font-extrabold">
              <th className="py-2 px-2 text-left align-middle w-2/6">Question</th>
              <th className="py-2 px-2 text-left align-middle w-1/6 cursor-pointer select-none" onClick={() => handleSort('category')}>
                Category
              </th>
              <th className="py-2 px-2 text-center align-middle w-1/12 cursor-pointer select-none" onClick={() => handleSort('difficulty')}>
                Difficulty
              </th>
              <th className="py-2 px-2 text-center align-middle w-1/12 cursor-pointer select-none" onClick={() => handleSort('dollarValue')}>
                Value
              </th>
              <th className="py-2 px-1 text-center align-middle w-1/12" title="Usage Count">
                <span role="img" aria-label="Usage">🔢</span>
              </th>
              <th className="py-2 px-1 text-center align-middle w-1/12" title="Rating">
                <span role="img" aria-label="Rating">⭐</span>
              </th>
              <th className="py-2 px-1 text-center align-middle w-1/12" title="Votes">
                <span role="img" aria-label="Votes">🗳️</span>
              </th>
              <th className="py-2 px-1 text-center align-middle w-1/12" title="Rate">👍/👎</th>
              <th className="py-2 px-1 text-center align-middle w-1/12" title="Actions">⋮</th>
            </tr>
          </thead>
          <tbody>
            {sortedQuestions.map((q, idx) => (
              <tr key={q.id} className={`border-b border-[#444] ${idx % 2 === 0 ? 'bg-[#232323]' : 'bg-[#181818]'} hover:bg-[#292929] min-h-[48px]`}>
                <td className="py-2 px-2 text-white align-middle text-sm font-medium max-w-[220px] break-words whitespace-pre-line">{q.text}</td>
                <td className="py-2 px-2 text-gray-300 align-middle">
                  <select
                    className="bg-[#333] text-white rounded px-1 py-1 border border-brand-yellow/30 focus:border-brand-yellow focus:outline-none text-xs"
                    value={q.categoryId}
                    disabled={updatingCategoryId === q.id}
                    onChange={async (e: ChangeEvent<HTMLSelectElement>) => {
                      const newCategoryId = e.target.value;
                      setUpdatingCategoryId(q.id!);
                      await questionService.updateQuestion(q.id!, { categoryId: newCategoryId });
                      setQuestions((prev) => prev.map(qq => qq.id === q.id ? { ...qq, categoryId: newCategoryId } : qq));
                      setUpdatingCategoryId(null);
                    }}
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 px-2 text-center align-middle">
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${
                    q.difficulty === 'easy' ? 'bg-green-500/20 text-green-400' :
                    q.difficulty === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {q.difficulty}
                  </span>
                </td>
                <td className="py-2 px-2 text-center text-brand-yellow font-bold align-middle text-xs">
                  {getDollarValue(q.difficulty, 0)}
                </td>
                <td className="py-2 px-1 text-center text-brand-yellow font-bold align-middle text-xs">{q.usageCount || 0}</td>
                <td className="py-2 px-1 text-center text-brand-yellow font-bold align-middle text-xs">{q.rating ? q.rating.toFixed(1) : '-'}</td>
                <td className="py-2 px-1 text-center text-brand-yellow font-bold align-middle text-xs">{q.ratingCount || 0}</td>
                <td className="py-2 px-1 text-center align-middle">
                  <div className="flex justify-center gap-1">
                    <button
                      onClick={() => handleVote(q.id!, 1)}
                      disabled={votedQuestions[q.id!]}
                      className={`text-sm ${votedQuestions[q.id!] ? 'opacity-50' : 'hover:text-green-400'}`}
                    >
                      👍
                    </button>
                    <button
                      onClick={() => handleVote(q.id!, -1)}
                      disabled={votedQuestions[q.id!]}
                      className={`text-sm ${votedQuestions[q.id!] ? 'opacity-50' : 'hover:text-red-400'}`}
                    >
                      👎
                    </button>
                  </div>
                </td>
                <td className="py-2 px-1 text-center align-middle">
                  <button onClick={() => openEditModal(q)} className="text-brand-yellow font-bold mr-4 hover:underline">Edit</button>
                  <button onClick={() => setDeleteId(q.id!)} className="text-red-400 font-bold hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Modal for Add/Edit Question */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-[#222] rounded-xl shadow-2xl p-8 max-w-lg w-full relative">
            <button
              className="absolute top-4 right-4 text-brand-yellow text-2xl font-bold hover:text-white"
              onClick={closeModal}
              aria-label="Close"
            >
              ×
            </button>
            <h3 className="text-2xl font-bold mb-6 text-brand-yellow">{editingQuestion ? 'Edit Question' : 'Add Question'}</h3>
            <div className="mb-4">
              <label className="block text-brand-yellow font-bold mb-2">Question Text</label>
              <textarea
                className="w-full bg-[#333] text-white rounded-lg px-4 py-3 border border-brand-yellow/30 focus:border-brand-yellow focus:outline-none"
                value={form.text}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setForm(f => ({ ...f, text: e.target.value }))}
                placeholder="Enter the question text"
                disabled={saving}
              />
            </div>
            <div className="mb-4">
              <label className="block text-brand-yellow font-bold mb-2">Answer</label>
              <input
                className="w-full bg-[#333] text-white rounded-lg px-4 py-3 border border-brand-yellow/30 focus:border-brand-yellow focus:outline-none"
                value={form.answer}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, answer: e.target.value }))}
                placeholder="Enter the correct answer"
                disabled={saving}
              />
            </div>
            <div className="mb-4">
              <label className="block text-brand-yellow font-bold mb-2">Category</label>
              <select
                className="w-full bg-[#333] text-white rounded-lg px-4 py-3 border border-brand-yellow/30 focus:border-brand-yellow focus:outline-none"
                value={form.categoryId}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setForm(f => ({ ...f, categoryId: e.target.value }))}
                disabled={saving}
              >
                <option value="">Select a category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-brand-yellow font-bold mb-2">Difficulty</label>
              <select
                className="w-full bg-[#333] text-white rounded-lg px-4 py-3 border border-brand-yellow/30 focus:border-brand-yellow focus:outline-none"
                value={form.difficulty}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setForm(f => ({ ...f, difficulty: e.target.value }))}
                disabled={saving}
              >
                {DIFFICULTIES.map(d => (
                  <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="flex items-center gap-2 text-brand-yellow font-bold">
                <input
                  type="checkbox"
                  checked={form.isFinalJeopardy}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, isFinalJeopardy: e.target.checked }))}
                  className="w-4 h-4 rounded border-brand-yellow/30 focus:ring-brand-yellow"
                  disabled={saving}
                />
                Final Jeopardy Question
              </label>
            </div>
            <div className="mb-4">
              <label className="flex items-center gap-2 text-brand-yellow font-bold">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, isActive: e.target.checked }))}
                  className="w-4 h-4 rounded border-brand-yellow/30 focus:ring-brand-yellow"
                  disabled={saving}
                />
                Active
              </label>
            </div>
            <div className="mb-6">
              <label className="block text-brand-yellow font-bold mb-2">Choices (optional, for multiple choice)</label>
              {form.choices.map((choice, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input
                    className="flex-1 bg-[#333] text-white rounded-lg px-4 py-3 border border-brand-yellow/30 focus:border-brand-yellow focus:outline-none"
                    value={choice}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleChoiceChange(idx, e.target.value)}
                    placeholder={`Choice ${idx + 1}`}
                    disabled={saving}
                  />
                  {form.choices.length > 1 && (
                    <button
                      className="text-red-400 font-bold px-2"
                      onClick={() => removeChoice(idx)}
                      disabled={saving}
                      type="button"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                className="mt-2 bg-[#5d7763] text-white font-bold px-4 py-2 rounded-lg hover:bg-[#5d7763]/90 transition-colors"
                onClick={addChoice}
                disabled={saving}
                type="button"
              >
                + Add Choice
              </button>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-brand-yellow text-black font-bold px-6 py-3 rounded-lg hover:bg-[#fccd6c]/90 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Question'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-[#222] rounded-xl shadow-2xl p-8 max-w-md w-full relative">
            <h3 className="text-2xl font-bold mb-6 text-brand-yellow">Delete Question</h3>
            <p className="text-white mb-8">Are you sure you want to delete this question? This cannot be undone.</p>
            <div className="flex gap-4 justify-end">
              <button
                className="bg-[#5d7763] text-white font-bold px-6 py-3 rounded-lg hover:bg-[#5d7763]/90 transition-colors"
                onClick={() => setDeleteId(null)}
              >
                Cancel
              </button>
              <button
                className="bg-red-600 text-white font-bold px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuestions; 